import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import * as d3 from 'd3';
import {GraphableSeries} from 'helpers/graphableTable';
import {useLayoutEffect, useRef, useState} from 'react';

/**
 * Deliberately small, axis-light chart (Sparkline-level) for rendering a
 * {@link GraphableSeries} parsed out of an agent answer table. Line/area for
 * time-ordered data, bars for categories. It carries a minimal y-axis (a few
 * ticks + faint gridlines), first/last x labels, and a hover tooltip with the
 * label and per-series values — no full axes.
 */

const SERIES_COLORS = [
  'var(--dig-color__primary__base)',
  'var(--dig-color__success__base--state-2)',
  'var(--dig-color__alert__on-surface)',
];

const AXIS_LINE = 'var(--dig-color__border__subtle)';
const AXIS_TEXT = 'var(--dig-color__text__subtle)';

const DEFAULT_HEIGHT = 120;
const FALLBACK_WIDTH = 320;
const MARGIN = {top: 8, right: 12, bottom: 18};
const Y_TICK_COUNT = 3;
const Y_AXIS_FONT_SIZE = 9;
// Gap between the tick label and the start of the plot area.
const Y_TICK_LABEL_GAP = 6;
// Approximate per-character width at Y_AXIS_FONT_SIZE for monospace-ish digits.
const Y_AXIS_CHAR_WIDTH = 5.5;

/** Track the rendered width so the SVG can fill its container responsively. */
const useContainerWidth = (fallback = FALLBACK_WIDTH) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(fallback);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width;
      if (next && next > 0) {
        setWidth(next);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return {ref, width};
};

const truncate = (label: string, max = 24): string =>
  label.length > max ? `${label.slice(0, max - 1)}…` : label;

/** Compact tick label: 1200 → "1.2k", 229 → "229". */
const formatTick = (n: number): string => (Math.abs(n) >= 1000 ? d3.format('~s')(n) : `${n}`);

const formatValue = (n: number): string => n.toLocaleString();

export const MiniChart = ({
  graph,
  height = DEFAULT_HEIGHT,
}: {
  graph: GraphableSeries;
  height?: number;
}) => {
  const {ref, width} = useContainerWidth();
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [hovered, setHovered] = useState<number | null>(null);
  const {kind, labels, series} = graph;

  if (labels.length === 0 || series.length === 0) {
    return null;
  }

  const innerH = Math.max(1, height - MARGIN.top - MARGIN.bottom);

  const allValues = series.flatMap((s) => s.values);
  const rawMin = d3.min(allValues) ?? 0;
  const rawMax = d3.max(allValues) ?? 1;
  // Anchor the baseline at zero (bars need it; lines read more honestly with it).
  const yMin = Math.min(0, rawMin);
  const yMax = rawMax === yMin ? yMin + 1 : rawMax;
  const yScale = d3.scaleLinear().domain([yMin, yMax]).range([innerH, 0]).nice();
  const yTicks = yScale.ticks(Y_TICK_COUNT);

  // Size the left margin to the widest tick label so the plot left-aligns
  // tightly regardless of whether ticks read "3" or "1.2k".
  const widestTick = Math.max(...yTicks.map((t) => formatTick(t).length), 1);
  const marginLeft = Math.ceil(widestTick * Y_AXIS_CHAR_WIDTH) + Y_TICK_LABEL_GAP;
  const innerW = Math.max(1, width - marginLeft - MARGIN.right);

  const n = labels.length;
  const xLine = d3
    .scaleLinear()
    .domain([0, Math.max(1, n - 1)])
    .range([0, innerW]);
  const band = innerW / n;
  // Pixel center of a data point within the plot area, by chart kind.
  const pointX = (idx: number): number => (kind === 'line' ? xLine(idx) : idx * band + band / 2);

  const handleMove = (event: React.MouseEvent<SVGRectElement>) => {
    const svg = svgRef.current;
    if (!svg) {
      return;
    }
    const x = event.clientX - svg.getBoundingClientRect().left - marginLeft;
    const clamped = Math.max(0, Math.min(innerW, x));
    const idx =
      kind === 'line'
        ? Math.round((clamped / innerW) * (n - 1))
        : Math.min(n - 1, Math.floor(clamped / band));
    setHovered(Math.max(0, Math.min(n - 1, idx)));
  };

  // Keep the tooltip within the container horizontally.
  const tooltipX =
    hovered == null ? 0 : Math.max(48, Math.min(width - 48, marginLeft + pointX(hovered)));
  // Keep the tooltip Y static at one of two fixed positions (top edge, or bottom
  // of the plot) rather than gluing it to the point — but flip to the bottom when
  // the hovered point is high so it never covers the line/dot.
  const hoveredValues = hovered == null ? [] : series.map((s) => s.values[hovered]);
  const topPointY = hovered == null ? 0 : MARGIN.top + yScale(Math.max(...hoveredValues));
  const placeBelow = topPointY < height / 2;
  const tooltipY = placeBelow ? height - MARGIN.bottom : 0;
  const tooltipTransform = placeBelow ? 'translate(-50%, -100%)' : 'translateX(-50%)';

  return (
    <Stack gap="4">
      {series.length > 1 && (
        <Box display="flex" flexWrap="wrap" style={{gap: 12}}>
          {series.map((s, i) => (
            <Box key={s.name} display="flex" alignItems="center" style={{gap: 6}}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 2,
                  backgroundColor: SERIES_COLORS[i % SERIES_COLORS.length],
                }}
              />
              <Text size="xsmall" color="subtle">
                {truncate(s.name)}
              </Text>
            </Box>
          ))}
        </Box>
      )}

      <div ref={ref} style={{width: '100%', position: 'relative'}}>
        <svg
          ref={svgRef}
          width={width}
          height={height}
          role="img"
          aria-label={`Chart of ${graph.labelHeader}`}
          style={{fontFamily: 'var(--dig-type__family__text)'}}
        >
          <g transform={`translate(${marginLeft},${MARGIN.top})`}>
            {yTicks.map((tick) => (
              <g key={tick} transform={`translate(0,${yScale(tick)})`}>
                <line x1={0} x2={innerW} stroke={AXIS_LINE} strokeWidth={1} />
                <text
                  x={-Y_TICK_LABEL_GAP}
                  y={3}
                  textAnchor="end"
                  fontSize={Y_AXIS_FONT_SIZE}
                  fill={AXIS_TEXT}
                >
                  {formatTick(tick)}
                </text>
              </g>
            ))}

            {kind === 'line'
              ? series.map((s, i) => {
                  const color = SERIES_COLORS[i % SERIES_COLORS.length];
                  const line = d3
                    .line<number>()
                    .x((_, idx) => xLine(idx))
                    .y((d) => yScale(d))
                    .curve(d3.curveMonotoneX);
                  const area = d3
                    .area<number>()
                    .x((_, idx) => xLine(idx))
                    .y0(innerH)
                    .y1((d) => yScale(d))
                    .curve(d3.curveMonotoneX);
                  return (
                    <g key={s.name}>
                      {series.length === 1 && (
                        <path d={area(s.values) ?? ''} fill={color} fillOpacity={0.12} />
                      )}
                      <path d={line(s.values) ?? ''} fill="none" stroke={color} strokeWidth={1.5} />
                    </g>
                  );
                })
              : (() => {
                  const color = SERIES_COLORS[0];
                  const barW = Math.max(1, band * 0.7);
                  const baseline = yScale(0);
                  return series[0].values.map((v, idx) => {
                    const top = yScale(Math.max(0, v));
                    const bottom = yScale(Math.min(0, v));
                    return (
                      <rect
                        key={`${labels[idx]}-${idx}`}
                        x={idx * band + (band - barW) / 2}
                        y={v >= 0 ? top : baseline}
                        width={barW}
                        height={Math.max(1, v >= 0 ? baseline - top : bottom - baseline)}
                        fill={color}
                        fillOpacity={hovered == null || hovered === idx ? 1 : 0.4}
                        rx={1}
                      />
                    );
                  });
                })()}

            {hovered != null && (
              <g pointerEvents="none">
                {kind === 'line' && (
                  <line
                    x1={pointX(hovered)}
                    x2={pointX(hovered)}
                    y1={0}
                    y2={innerH}
                    stroke={AXIS_LINE}
                    strokeWidth={1}
                  />
                )}
                {kind === 'line' &&
                  series.map((s, i) => (
                    <circle
                      key={s.name}
                      cx={pointX(hovered)}
                      cy={yScale(s.values[hovered])}
                      r={3}
                      fill={SERIES_COLORS[i % SERIES_COLORS.length]}
                    />
                  ))}
              </g>
            )}

            <rect
              x={0}
              y={0}
              width={innerW}
              height={innerH}
              fill="transparent"
              pointerEvents="all"
              onMouseMove={handleMove}
              onMouseLeave={() => setHovered(null)}
            />
          </g>

          <text x={marginLeft} y={height - 5} textAnchor="start" fontSize={10} fill={AXIS_TEXT}>
            {truncate(labels[0])}
          </text>
          {labels.length > 1 && (
            <text
              x={width - MARGIN.right}
              y={height - 5}
              textAnchor="end"
              fontSize={10}
              fill={AXIS_TEXT}
            >
              {truncate(labels[labels.length - 1])}
            </text>
          )}
        </svg>

        {hovered != null && (
          <Box
            position="absolute"
            borderRadius="Medium"
            boxShadow="Raised"
            paddingX="8"
            paddingY="4"
            style={{
              left: tooltipX,
              top: tooltipY,
              transform: tooltipTransform,
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
              backgroundColor: 'var(--dig-color__background__base)',
              border: '1px solid var(--dig-color__border__subtle)',
              zIndex: 1,
            }}
          >
            <Text size="xsmall" color="subtle" isBold>
              {labels[hovered]}
            </Text>
            {series.map((s, i) => (
              <Box key={s.name} display="flex" alignItems="center" style={{gap: 6}}>
                {series.length > 1 && (
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 2,
                      backgroundColor: SERIES_COLORS[i % SERIES_COLORS.length],
                    }}
                  />
                )}
                <Text size="xsmall">
                  {series.length > 1
                    ? `${truncate(s.name, 16)}: ${formatValue(s.values[hovered])}`
                    : `${formatValue(s.values[hovered])} ${truncate(s.name, 28)}`}
                </Text>
              </Box>
            ))}
          </Box>
        )}
      </div>
    </Stack>
  );
};
